import { useState } from 'react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useJobCostOverview } from '../hooks/useJobCostOverview';
import { useJobCostReport } from '../hooks/useJobCostReport';
import { CostOverviewHeader } from './CostOverviewHeader';
import { CostOverviewTable } from './CostOverviewTable';
import { CostOverviewSidebar } from './CostOverviewSidebar';
import { CostEntryForm } from './CostEntryForm';
import { ChargeEntryForm } from './ChargeEntryForm';
import { PaymentEntryForm } from './PaymentEntryForm';
import type { JobDetailDto } from '../types';
import type { CostOverviewLineItemDto, ChargeOverviewLineItemDto, PaymentOverviewLineItemDto } from '../types/jobCostOverview';
import type { JobCost } from '../types/jobCost';
import type { JobCharge } from '../types/jobCharge';
import type { JobPayment } from '../types/jobPayment';

interface JobCostOverviewProps {
  jobId: number;
  job: JobDetailDto;
}

export function JobCostOverview({ jobId }: JobCostOverviewProps) {
  const { data, isLoading, error, refetch } = useJobCostOverview(jobId);
  const { openPdf, isLoading: isPrinting } = useJobCostReport(jobId);

  // Cost dialog state
  const [isCostFormOpen, setIsCostFormOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<JobCost | null>(null);

  // Charge dialog state
  const [isChargeFormOpen, setIsChargeFormOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState<JobCharge | null>(null);

  // Payment dialog state
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<JobPayment | null>(null);

  const handleAddCost = () => {
    setEditingCost(null);
    setIsCostFormOpen(true);
  };

  const handleEditCost = (cost: CostOverviewLineItemDto) => {
    // Map overview line item to the shape the edit form expects
    setEditingCost({
      id: cost.id,
      jobId,
      costCategoryId: cost.costCategoryId,
      costCategoryName: cost.costCategoryName,
      isForecast: false,
      paymentDate: cost.paymentDate,
      checkNumber: cost.checkNumber,
      paidTo: cost.paidTo,
      description: cost.description,
      amount: cost.amount,
      taxRate: cost.taxRate,
      taxAmount: cost.taxAmount,
      total: cost.total,
      createdAt: '',
      createdByName: '',
    } as JobCost);
    setIsCostFormOpen(true);
  };

  const handleAddCharge = () => {
    setEditingCharge(null);
    setIsChargeFormOpen(true);
  };

  const handleEditCharge = (charge: ChargeOverviewLineItemDto) => {
    setEditingCharge({
      id: charge.id,
      jobId,
      isForecast: false,
      chargeDate: charge.chargeDate,
      amount: charge.amount,
      description: charge.description,
      isFee: false,
      isInterest: false,
      hasImages: charge.hasImages,
      imageCount: 0,
      createdAt: '',
      createdByName: '',
    } as JobCharge);
    setIsChargeFormOpen(true);
  };

  const handleAddPayment = () => {
    setEditingPayment(null);
    setIsPaymentFormOpen(true);
  };

  const handleEditPayment = (payment: PaymentOverviewLineItemDto) => {
    setEditingPayment({
      id: payment.id,
      jobId,
      paymentTypeId: 0, // Will be loaded by form
      paymentTypeName: payment.paymentTypeName,
      isForecast: false,
      paymentDate: payment.paymentDate,
      amount: payment.amount,
      referenceNumber: payment.referenceNumber,
      isPending: payment.isPending,
      isInterest: false,
      hasImages: payment.hasImages,
      imageCount: 0,
      createdAt: '',
      createdByName: '',
    } as JobPayment);
    setIsPaymentFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsCostFormOpen(false);
    setIsChargeFormOpen(false);
    setIsPaymentFormOpen(false);
    setEditingCost(null);
    setEditingCharge(null);
    setEditingPayment(null);
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" label="Loading cost overview..." />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorAlert error={error || 'Failed to load cost overview'} className="m-4" />;
  }

  return (
    <div className="p-3">
      <CostOverviewHeader header={data.header} onPrint={openPdf} isPrinting={isPrinting} />

      <div className="flex gap-3" style={{ height: 'calc(100vh - 360px)' }}>
        {/* Left: Cost table (65%) */}
        <div className="w-[65%] flex flex-col min-h-0">
          <CostOverviewTable
            jobId={jobId}
            costs={data.costs}
            totals={data.costTotals}
            onAddCost={handleAddCost}
            onEditCost={handleEditCost}
          />
        </div>

        {/* Right: Sidebar panels (35%) */}
        <div className="w-[35%] overflow-y-auto">
          <CostOverviewSidebar
            jobId={jobId}
            data={data}
            onAddCharge={handleAddCharge}
            onEditCharge={handleEditCharge}
            onAddPayment={handleAddPayment}
            onEditPayment={handleEditPayment}
          />
        </div>
      </div>

      {/* Entry Form Dialogs */}
      <CostEntryForm
        jobId={jobId}
        isOpen={isCostFormOpen}
        onClose={() => { setIsCostFormOpen(false); setEditingCost(null); }}
        onSuccess={handleFormSuccess}
        editingCost={editingCost}
        isForecast={false}
      />

      <ChargeEntryForm
        jobId={jobId}
        isOpen={isChargeFormOpen}
        onClose={() => { setIsChargeFormOpen(false); setEditingCharge(null); }}
        onSuccess={handleFormSuccess}
        editingCharge={editingCharge}
        isForecast={false}
      />

      <PaymentEntryForm
        jobId={jobId}
        isOpen={isPaymentFormOpen}
        onClose={() => { setIsPaymentFormOpen(false); setEditingPayment(null); }}
        onSuccess={handleFormSuccess}
        editingPayment={editingPayment}
        isForecast={false}
      />
    </div>
  );
}
